import { Outlet } from "react-router-dom";
import { Suspense, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Sidebar from "@/components/Sidebar";
import DemoReturnBanner from "@/components/DemoReturnBanner";
import DemoBanner from "@/components/demo/DemoBanner";
import DemoOverlayController from "@/components/demo/DemoOverlayController";
import { useDemo } from "@/contexts/DemoContext";
import { useSidebar } from "@/context/SidebarContext";

/**
 * Shared layout for all authenticated pages.
 * Renders: background gradient, Header, Sidebar, and content area with dynamic margin.
 * Individual pages render inside <Outlet /> without needing to import Header/Sidebar.
 */
export default function AppLayout() {
  const { isExpanded } = useSidebar();
  const [headerHeight, setHeaderHeight] = useState(64);
  const location = useLocation();
  const isDemo = location.pathname.startsWith("/demo");
  const { isInDemo } = useDemo();
  const DEMO_BANNER_HEIGHT = 52;

  // Track header height with ResizeObserver for dynamic spacer
  useEffect(() => {
    const header = document.querySelector("header");
    if (!header) return;

    const observer = new ResizeObserver(([entry]) => {
      setHeaderHeight(entry.contentRect.height);
    });
    observer.observe(header);
    setHeaderHeight(header.offsetHeight);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen relative overflow-x-hidden">
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-[#DFCEFD] via-[#FCDFCB] to-[#DFCEFD] -z-10" />

      {/* Decorative blur — hidden on mobile */}
      <div className="hidden md:block fixed left-[247px] top-[2592px] w-[575px] h-[1350px] rounded-full bg-gradient-to-br from-[#E9AA84]/28 to-[#BA73FF]/28 blur-[98.65px] -z-5" />

      {!isDemo && <Header />}
      <Sidebar />
      {!isDemo && <DemoBanner topOffset={headerHeight} />}

      {/* Dynamic spacer matching header height */}
      {!isDemo && (
        <div
          className="transition-all duration-300"
          style={{ height: `${headerHeight + (isInDemo ? DEMO_BANNER_HEIGHT : 0)}px` }}
        />
      )}

      {/* Main content area with sidebar margin (full-bleed for demo routes) */}
      <div
        className={
          isDemo
            ? "transition-all duration-300 ease-in-out"
            : `transition-all duration-300 ease-in-out px-4 md:px-6 pt-8 md:pt-14 pb-12 ml-0 ${
                isExpanded ? "md:ml-[263px]" : "md:ml-[88px]"
              }`
        }
      >
        <Suspense fallback={
          <div className="flex items-center justify-center py-32">
            <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        }>
          <Outlet />
        </Suspense>
      </div>
      <DemoReturnBanner />
      <DemoOverlayController />
    </div>
  );
}
